import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

/**
 * Single source of truth for status -> color across order status, payment
 * status, and transaction status. Previously each feature reinvented this
 * mapping (plain colored divs, Badge+className, Badge+variant) independently.
 */
const STATUS_COLOR_MAP: Record<string, string> = {
  // order status
  pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  in_progress: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  completed: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  cancelled: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  hold: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400",
  // payment status
  paid: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  not_paid: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  // transaction status
  failed: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  success: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
};

const DEFAULT_COLOR =
  "bg-neutral-100 text-neutral-800 dark:bg-neutral-800 dark:text-neutral-300";

function normalize(status: string) {
  return status?.trim().toLowerCase().replace(/[\s-]+/g, "_");
}

export interface StatusBadgeProps {
  status: string;
  className?: string;
}

export function StatusBadge({ status, className }: StatusBadgeProps) {
  const color = STATUS_COLOR_MAP[normalize(status)] ?? DEFAULT_COLOR;
  const label = status?.replace(/[_-]+/g, " ") ?? "-";

  return (
    <Badge className={cn("border-transparent capitalize", color, className)}>
      {label}
    </Badge>
  );
}

export interface PaymentMethodBadgeProps {
  method: string;
  className?: string;
}

export function PaymentMethodBadge({ method, className }: PaymentMethodBadgeProps) {
  return (
    <Badge variant="outline" className={cn("capitalize", className)}>
      {method}
    </Badge>
  );
}
